import {useMemo, useEffect} from 'react';

import sdk from '@/sdk';
import * as store from '@/store';
import {feLogger, createDataSheetBlockReport} from '@/buried';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {routerPublicPath} from '@/config/routers';
import {useApiMessageHandler} from '@/kuTools/sdk/hooks/useApiMessageHandler';
import {getMorphoService, serviceNames} from '@/rte/editor';

const {
    useIsInGray: useIsOpenSubDocList,
} = initGray(GRAY_RELEASE_KEYS.subDocList);

export const useDataSheetConfig = () => {
    const userGuid = sdk.as.user?.userGuid;
    const repository = store.getData(store.KEYS.CUR_REPOSITORY);
    const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};

    return useMemo(() => {
        return {
            async createDataSheet(type: string, name: string, chartType: string) {
                try {
                    if (!type || !name) {
                        throw new Error('缺少参数 type 或 name');
                    }

                    const ksServer = await sdk.getKsServer(repository);
                    const {
                        authUrl, readUrl, datasheetId, viewId,
                    } = await ksServer.createDataSheetBlock(docGuid, type, name, chartType);

                    if (authUrl && readUrl) {
                        feLogger(createDataSheetBlockReport({
                            'page_id': docGuid,
                            'user_id': userGuid,
                            viewType: type,
                            'action': 'create',
                        }));

                        return {
                            sourceUrlEdit: authUrl,
                            sourceUrlPreview: readUrl,
                            datasheetId,
                            viewId,
                        };
                    } else {
                        throw new Error('链接为空');
                    }
                } catch (error) {
                    console.error(error);
                    throw error;
                }
            },
            origin: 'placeholder',
            async createDataSheetUrl(targetDocId: string, editUrl: string) {
                try {
                    if (!targetDocId || !editUrl) {
                        throw new Error('缺少参数 targetDocId 或 editUrl');
                    }

                    const ksServer = await sdk.getKsServer(repository);
                    const res = await ksServer.copyDataSheetBlock(targetDocId, editUrl);

                    if (Array.isArray(res) && res.length > 0) {
                        const {authUrl, readUrl, nodeId, viewId} = res[0];

                        if (!authUrl || !readUrl || !nodeId || !viewId) {
                            throw new Error('返回结果为空');
                        }

                        feLogger(createDataSheetBlockReport({
                            'page_id': docGuid,
                            'user_id': userGuid,
                            'action': 'copy',
                        }));

                        return {
                            sourceUrlEdit: authUrl,
                            sourceUrlPreview: readUrl,
                            datasheetId: nodeId,
                            viewId,
                        };
                    } else {
                        throw new Error('返回结果为空');
                    }
                } catch (error) {
                    console.error(error);
                    throw error;
                }
            },
            async getChartData(datasheetId: string, viewId: string) {
                try {
                    if (!datasheetId || !viewId) {
                        throw new Error('缺少参数 datasheetId 或 viewId');
                    }

                    const ksServer = await sdk.getKsServer(repository);
                    const {
                        records, viewName, fieldMap,
                    } = await ksServer.getChartData(docGuid, datasheetId, viewId);

                    if (Array.isArray(records) && records.length > 0) {
                        return {
                            record: records,
                            viewName: viewName || '',
                            fieldMap: fieldMap || {},
                        };
                    } else {
                        throw new Error('图表数据为空');
                    }

                } catch (error) {
                    console.error(error);
                    throw error;
                }
            },
            chartEnable: true, // 数据表图表插件小流量下线
        };
    }, [repository, docGuid, userGuid]);
};

interface ISubDocListConfig {
    enableInsert?: boolean;
}

export const useSubDocListConfig = (options?: ISubDocListConfig) => {
    const {enableInsert = false} = options || {};

    useApiMessageHandler();

    const isOpenSubDocList = useIsOpenSubDocList();
    const showInserts = enableInsert && !!isOpenSubDocList;

    useEffect(() => {
        try {
            const config = getMorphoService(serviceNames.IConfigService);
            if (config) {
                const preValue = config.state.embed.getValue();

                if (showInserts && !preValue?.subDocList?.showInserts && preValue?.subDocList) {
                    const newEmbed = {...preValue};
                    newEmbed.subDocList.showInserts = true;
                    config.state.embed.next(newEmbed);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, [showInserts]);

    return useMemo(() => {
        return {
            origin: `${window.location.origin}`,
            source: `${window.location.origin}${routerPublicPath}/indexSubDocCard.html`,
            showInserts,
        };
    }, [showInserts]);
};
